import { useEffect, useState } from 'react';
import * as stores from '../../../api/stores';
import ReactJson from 'react-json-view';
import { Select } from './Select';
// import { changeProfile } from '../../../api';
import { PlaycastDefaultProfiles, PlaycastPeerClient } from '@playcastdotio/Peer';
import { PeerStatsComponent } from '@playcastdotio/PeerComponents';
import { Button } from '@playcastdotio/Shadcn';
import { getPeer, hardResetDev, qualityManager } from '../../../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBug } from '@fortawesome/pro-solid-svg-icons';
import { isMobileDevice } from '../../../api/utility';

export const DevOverlay = () => {
  const [show, setShow] = useState(localStorage.getItem('devOverlay') === 'true' || false);
  const [reactStores, setReactStores] = useState<typeof stores>(stores);
  const [shouldOverlay, setShouldOverlay] = useState(false);
  const [clientPeer, setClientPeer] = useState<PlaycastPeerClient>();

  useEffect(() => {
    console.log('DevOverlay mounted');
    const listeners = {};
    Object.keys(stores).forEach((storeName) => {
      const localStore = stores[storeName];
      if (localStore.addListener === undefined) return;
      console.log('Adding listener for', storeName);
      listeners[storeName] = localStore.addListener((state: unknown) => {
        setReactStores((prev) => ({ ...prev, [storeName]: state }));
      });
      setReactStores((prev) => ({ ...prev, [storeName]: localStore.currentState }));
    });
    listeners['qualityManager'] = qualityManager.preferredQualityState.addListener((state) => {
      setReactStores((prev) => ({ ...prev, qualityManager: state }));
    });
    setReactStores((prev) => ({ ...prev, qualityManager: qualityManager.preferredQualityState.currentState }));

    return () => {
      Object.keys(listeners).forEach((storeName) => {
        const localStore = stores[storeName];
        if (localStore) {
          if (localStore.removeListener === undefined) return;
          localStore.removeListener(listeners[storeName]);
        } else {
          if (storeName === 'qualityManager') {
            qualityManager.preferredQualityState.removeListener(listeners[storeName]);
          }
        }
      });
    };
  }, []);

  useEffect(() => {
    const joinStateListener = stores.joinState.addListener((state) => {
      setShouldOverlay(state.state === 'hostConnected');
      setClientPeer(getPeer());
    });
    setShouldOverlay(stores.joinState.currentState.state === 'hostConnected');

    return () => {
      stores.joinIdState.removeListener(joinStateListener);
    };
  }, []);

  // show if ctrl+f12 is pressed
  window.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.key === 'F12') {
      localStorage.setItem('devOverlay', (!show).toString());
      setShow(!show);
    }
  });

  const handleDebugCopyToClipboard = () => {
    const redactedData = {
      ...reactStores,
      authStateStore: {
        ...reactStores.authStateStore,
        accessToken: reactStores.authStateStore ? '<redacted>' : '',
      },
    };
    const debugDump = `
---

<details>
<summary>
<h3>Debug Dump: Player</h3>
</summary>

\`\`\`json
${JSON.stringify(redactedData, null, 2)}
\`\`\`

Quality Control:
\`\`\`json
${JSON.stringify(qualityManager.preferredQualityState.currentState, null, 2)}
\`\`\`
</details>

---
`;
    navigator.clipboard.writeText(debugDump);
  };

  const floatingStyle: React.CSSProperties = {
    height: '100vh',
    overflowY: 'scroll',
    position: 'fixed',
    minWidth: '40vw',
    top: 0,
    left: 0,
    zIndex: 9999,
    backgroundColor: 'rgba(11, 28, 44, 0.8)',
  };
  const floatingButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 9999,
  };

  const [selectedQuality, setSelectedQuality] = useState<PlaycastDefaultProfiles | 'auto'>(
    qualityManager.preferredQualityState.currentState.auto ? 'auto' : qualityManager.preferredQualityState.currentState.profile,
  );

  useEffect(() => {
    const listener = qualityManager.preferredQualityState.addListener((state) => {
      setSelectedQuality(state.auto ? 'auto' : state.profile);
    });
    return () => {
      qualityManager.preferredQualityState.removeListener(listener);
    };
  }, []);

  const dropdownItems: { label: string; value: PlaycastDefaultProfiles | 'auto' }[] = [
    {
      label: 'Auto',
      value: 'auto',
    },
    {
      label: '1080p',
      value: 'hostHigh',
    },
    {
      label: '720p',
      value: 'hostMedium',
    },
    {
      label: '360p',
      value: 'hostLow',
    },
  ];

  useEffect(() => {
    if (selectedQuality === 'auto') {
      const isMobile = isMobileDevice();
      qualityManager.setQuality(isMobile ? 'hostLow' : 'hostMedium', true);
    } else {
      qualityManager.setQuality(selectedQuality, false);
    }
  }, [selectedQuality]);

  const handleCloseClicked = () => {
    localStorage.setItem('devOverlay', 'false');
    setShow(false);
  };

  return (
    show &&
    (shouldOverlay ? (
      <div className='dev-overlay__content pb-4 pr-0' style={floatingStyle}>
        <div className='dev-overlay__section'>
          <ul>
            <div className='align-center flex justify-between'>
              <h1 className='align-center mt-4 px-2 text-white'>Quality</h1>
              <Select
                className='mt-2'
                data={dropdownItems}
                selected={selectedQuality}
                onChange={(value) => setSelectedQuality(value as PlaycastDefaultProfiles)}
                placeholder='Quality'
              />
              <div className='dev-overlay__header'>
                <Button onClick={handleDebugCopyToClipboard}>
                  <FontAwesomeIcon icon={faBug} />
                </Button>
              </div>
              <div className='dev-overlay__header'>
                <Button onClick={handleCloseClicked}>Close</Button>
              </div>
              <div className='dev-overlay__header'>
                <Button onClick={hardResetDev}>Reset Video</Button>
              </div>
            </div>
          </ul>
          <ul>
            <ReactJson
              name={false}
              src={reactStores}
              theme={'harmonic'}
              collapseStringsAfterLength={80}
              collapsed={1}
              indentWidth={2}
              style={{ fontSize: '16px', backgroundColor: 'rgba(0, 0, 0, 0)' }}
            />
          </ul>
          <ul>{clientPeer && <PeerStatsComponent clientPeer={clientPeer} />}</ul>
        </div>
      </div>
    ) : (
      <div className='dev-overlay text-white'>
        <div className='dev-overlay__header'>
          <Button onClick={handleDebugCopyToClipboard}>
            <FontAwesomeIcon icon={faBug} />
          </Button>
        </div>
        <div className='dev-overlay__header' style={floatingButtonStyle}>
          <Button onClick={handleCloseClicked}>Close</Button>
        </div>
        <div className='dev-overlay__content'>
          <div className='dev-overlay__section'>
            <ul>
              <ReactJson
                name={false}
                src={reactStores}
                theme={'harmonic'}
                collapseStringsAfterLength={80}
                collapsed={1}
                indentWidth={2}
                style={{ fontSize: '16px' }}
              />
            </ul>
          </div>
        </div>
      </div>
    ))
  );
};
